/**
 * @file Rendu instancie des cibles : toutes les cibles (toutes variantes) dans UN lot (PartBatch / BatchedMesh : 2 draw calls au total) ; flash de coup, chute/eclatement a la mort.
 */
import { Color, Euler, Matrix4, Quaternion, Vector3 } from 'three';
import type { Scene } from 'three';
import type { TargetDef } from '@config/types';
import { MergeBuilder } from '../factories/MergeBuilder';
import { PartBatch } from '../gpu/PartBatch';
import type { PartHandle, PartSource } from '../gpu/PartBatch';
import type { ResourceTracker } from '../gpu/ResourceTracker';
import type { TargetFactory, TargetProto } from '../factories/TargetFactory';
import type { ToonKit } from '../toon/toon';

/**
 * Donnees d'affichage d'une cible pour la frame (lues dans la SimView, deja interpolees).
 * `scale` = multiplicateur RELATIF au prototype (les geometries portent deja def.visual.scale) : 1 normalement, 1.6 pour un boss.
 */
export interface TargetFrame {
  id: number;
  def: TargetDef;
  variant: number;
  x: number;
  y: number;
  rot: number;
  scale: number;
  hitFlash: number;
  /** Secondes restantes d'animation de mort (0 = vivante). */
  dying: number;
  /** Cible tirable (les decors du hub ne le sont pas : aucune barre / reticule). */
  shootable: boolean;
  time: number;
}

/** Duree totale de l'animation de mort (identique a core/combat/damage DYING_SECONDS). */
const DYING_SECONDS = 0.45;
/** Instances simultanees maximales (cibles + decors). */
const MAX_INSTANCES = 320;
/** Eclaircissement au coup (flash blanc) : franc pour que chaque impact se lise sans secouer l'ecran, sans eblouir via le bloom (cibles pales). */
const FLASH_GAIN = 1.0;
/** Ecrasement elastique au coup (squash & stretch), decroit avec hitFlash. */
const SQUASH_XZ = 0.13;
const SQUASH_Y = 0.1;
const FALL_MAX_RAD = (85 * Math.PI) / 180;
/** Les parties lumineuses (palette.glow) sont eclaircies : le lot partage un seul materiau (pas d'emissive par piece). */
const GLOW_GAIN = 1.45;

interface Live {
  key: string;
  handle: PartHandle;
  stamp: number;
  /** Temps d'animation de la premiere apparition (animation « pop » de l'herbe a chat). */
  born: number;
}

const WIND_THEMES: ReadonlySet<string> = new Set(['fir', 'palm', 'oak', 'cactus', 'bush', 'flowers', 'catnip']);

/**
 * Strategie :
 *  - Chaque prototype (defId#variant) est refusionne en UNE geometrie a couleurs de sommets (les pieces emissives sont eclaircies)
 *    et enregistre dans un PartBatch : toutes les variantes de toutes les cibles = 2 draw calls (remplissage + contour).
 *  - Une instance par entite vivante (creee a la premiere apparition, retiree quand l'entite disparait). Couleur d'instance =
 *    flash de coup (lerp vers 1.8) et scintillement des cibles `sparkle`.
 *  - Matrice d'instance : position (x, 0, -y), lacet, echelle (squash au coup), mort : arbre -> bascule (rotation X jusqu'a 85 deg
 *    sur 0.45 s) + descente ; monstre/minerai/rocher -> gonflement puis echelle -> 0 ; vent sur arbres ; monstres : rebond.
 *  - Chaque frame : begin() ouvre une generation, draw(frame) met a jour l'instance, end() retire les instances non revues.
 */
export class InstancedTargets {
  private readonly batch: PartBatch;
  private readonly live = new Map<number, Live>();
  private stamp = 0;
  private readonly color = new Color();
  private readonly matrix = new Matrix4();
  private readonly euler = new Euler();
  private readonly quat = new Quaternion();
  private readonly pos = new Vector3();
  private readonly scl = new Vector3();

  /** `opts` (facultatif) : dimensionne le lot pour de petites scenes (galerie de dev) ; le jeu garde les valeurs par defaut. */
  constructor(
    scene: Scene,
    private readonly factory: TargetFactory,
    kit: ToonKit,
    private readonly res: ResourceTracker,
    opts: { maxInstances?: number; vertexCapacity?: number } = {},
  ) {
    this.batch = new PartBatch(scene, kit, opts.maxInstances ?? MAX_INSTANCES, opts.vertexCapacity);
  }

  begin(): void {
    this.stamp++;
  }

  draw(f: TargetFrame): void {
    // une maison qui s'ecroule est dessinee en morceaux par fx/HouseCollapse : plus d'instance entiere
    if (f.dying > 0 && f.def.visual.theme === 'house') return;
    const proto = this.factory.proto(f.def, f.variant);
    const key = `t:${proto.key}`;
    this.batch.register(key, () => this.source(proto));
    let live = this.live.get(f.id);
    if (live && live.key !== key) {
      this.batch.remove(live.handle);
      this.live.delete(f.id);
      live = undefined;
    }
    if (!live) {
      const handle = this.batch.add(key);
      if (!handle) return;
      live = { key, handle, stamp: this.stamp, born: f.time };
      this.live.set(f.id, live);
    }
    live.stamp = this.stamp;

    // --- matrice
    const theme = f.def.visual.theme;
    const family = f.def.family;
    const living = family === 'monster' || family === 'boss';
    let sx = f.scale;
    let sy = f.scale;
    let sz = f.scale;
    let yOff = 0;
    let pitch = 0;
    let roll = 0;
    if (f.dying > 0) {
      const p = Math.min(1, Math.max(0, 1 - f.dying / DYING_SECONDS));
      if (family === 'tree') {
        pitch = FALL_MAX_RAD * p * p;
        yOff = -0.25 * p * f.scale;
        const shrink = 1 - Math.min(1, Math.max(0, (p - 0.75) / 0.25));
        sx *= shrink;
        sy *= shrink;
        sz *= shrink;
      } else {
        const k = p < 0.25 ? 1 + 0.35 * (p / 0.25) : 1.35 * Math.pow(1 - (p - 0.25) / 0.75, 2);
        sx *= k;
        sy *= k;
        sz *= k;
      }
    } else {
      const h = f.hitFlash;
      sx *= 1 + SQUASH_XZ * h;
      sz *= 1 + SQUASH_XZ * h;
      sy *= 1 - SQUASH_Y * h;
      if (WIND_THEMES.has(theme)) roll = Math.sin(f.time * 1.3 + f.id) * 0.03;
      if (living) {
        const w = Math.sin(f.time * 6 + f.id * 1.3);
        yOff = Math.abs(w) * 0.09 * f.scale;
        sy *= 1 + w * 0.04;
        sx *= 1 - w * 0.025;
        sz *= 1 - w * 0.025;
      }
    }
    if (theme === 'catnip' && f.dying <= 0) {
      // surgit en rebondissant (ressort amorti : 0 -> depassement -> 1), puis respire doucement
      const k = Math.max(0, f.time - live.born);
      const pop = 1 - Math.exp(-k * 8) * Math.cos(k * 15);
      const breathe = 1 + 0.04 * Math.sin(f.time * 3 + f.id);
      sx *= pop * breathe;
      sy *= pop / breathe;
      sz *= pop * breathe;
    }
    const yaw = living ? f.rot + Math.PI / 2 : f.rot;
    this.pos.set(f.x, yOff, -f.y);
    this.euler.set(pitch, yaw, roll, 'YXZ');
    this.quat.setFromEuler(this.euler);
    this.scl.set(sx, sy, sz);
    this.matrix.compose(this.pos, this.quat, this.scl);

    // --- couleur d'instance (flash de coup, scintillement des cibles `sparkle`)
    const twinkle = f.def.visual.sparkle ? 0.5 + 0.5 * Math.sin(f.time * 5 + f.id * 1.7) : 0;
    const v = (1 + FLASH_GAIN * f.hitFlash) * (1 + 0.18 * twinkle);
    this.batch.set(live.handle, this.matrix, this.color.setRGB(v, v, v));
  }

  end(): void {
    for (const [id, l] of this.live) {
      if (l.stamp === this.stamp) continue;
      this.batch.remove(l.handle);
      this.live.delete(id);
    }
  }

  /** Nombre de geometries de prototype enregistrees dans le lot (diagnostic). */
  get batchCount(): number {
    return this.batch.geometryCount;
  }

  dispose(): void {
    this.live.clear();
    this.batch.dispose();
  }

  // ------------------------------------------------------------------------------------------------------------------

  /** Refusionne les ProtoPart (une par couleur) en une geometrie a couleurs de sommets + source de contour. */
  private source(proto: TargetProto): PartSource {
    const builder = new MergeBuilder();
    for (const part of proto.parts) {
      const c = new Color(part.color);
      if (part.emissive) c.multiplyScalar(GLOW_GAIN);
      builder.addRaw(part.geometry, c);
    }
    const fill = builder.fill('main');
    if (!fill) throw new Error(`InstancedTargets: prototype vide (${proto.key})`);
    void this.res;
    return { fill, hull: builder.hull(), temp: true };
  }
}
